import { c as _c } from "react/compiler-runtime";
// The one booklet page (desktop detail, Settings, Usage) and the one panel head (pages and the sheet).
// BookPage: a section in #row with `data-page` (Book reveals it when it appears); Escape closes the topmost one.
// PageHead: sticky head (data-t=head) — optional meta line, the title (data-t=title), Close when closable, the grip
// in the sheet. Styles: book.css `.page`, `.head`.
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LinkButton } from "./link-button";

/** `busy`: the page's data is loading (aria-busy: book.css draws the loader line). */
type PageProps = {
  t: string;
  page: string;
  label: string;
  className: string;
  onClose?: () => void;
  id?: string;
  busy?: boolean;
  children: ReactNode;
};

/** Pages listening for Escape, most recent last: Escape closes only the last one. */
const escapes: (() => void)[] = [];
export function BookPage(t0) {
  const $ = _c(11);
  const {
    t,
    page,
    label,
    className,
    onClose,
    id,
    busy,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] !== onClose) {
    t1 = () => {
      if (!onClose) {
        return;
      }
      escapes.push(onClose);
      const onKey = e => e.key === "Escape" && escapes.at(-1) === onClose && onClose();
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        escapes.splice(escapes.indexOf(onClose), 1);
      };
    };
    t2 = [onClose];
    $[0] = onClose;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = `page ${className}`;
  const t4 = busy || undefined;
  let t5;
  if ($[3] !== children || $[4] !== id || $[5] !== label || $[6] !== page || $[7] !== t || $[8] !== t3 || $[9] !== t4) {
    t5 = <section className={t3} data-t={t} data-id={id} data-page={page} aria-label={label} aria-busy={t4}>{children}</section>;
    $[3] = children;
    $[4] = id;
    $[5] = label;
    $[6] = page;
    $[7] = t;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** `actions`: icon buttons (IconButton) to the right of the title. */
export function PageHead(t0) {
  const $ = _c(13);
  const {
    title,
    meta,
    onClose,
    grip,
    actions
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== grip) {
    t1 = grip && <div className="grip" />;
    $[0] = grip;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== meta) {
    t2 = meta && <div className="meta"><span className="mono">{meta}</span></div>;
    $[2] = meta;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== actions || $[5] !== onClose || $[6] !== t || $[7] !== title) {
    t3 = actions || onClose ? <div className="title-row"><h1 data-t="title">{title}</h1><div className="actions">{actions}{onClose && <LinkButton onClick={onClose}>{t("panel.close")}</LinkButton>}</div></div> : <h1 data-t="title">{title}</h1>;
    $[4] = actions;
    $[5] = onClose;
    $[6] = t;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <div className="head" data-t="head">{t1}{t2}{t3}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}